/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { Search, Filter, History, Users, Clock, CheckCircle2, Percent, MessageCircle, Mail, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, EyeOff, RotateCcw, RefreshCw, PauseCircle } from 'lucide-react';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

function Primeiro15Consulta({ embedded = false }) {
  const navigate = useNavigate();

  // --- Estados do Histórico e Modal ---
  const [showModal, setShowModal] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [historicoFiltrado, setHistoricoFiltrado] = useState([]);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState('');
  const [loadingAcaoId, setLoadingAcaoId] = useState(null);
  
  // Filtros internos do Modal
  const [filtroDataLog, setFiltroDataLog] = useState('');
  const [filtroTipoLog, setFiltroTipoLog] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = perfilNoModulo(user.perfil, 'rh');
  const isAdminOrRH = role === 'Admin' || role === 'RH' || role === 'Diretoria';
  const isAdmin = user.perfil === 'Admin';
  // Só o RH (e o Admin, que tem bypass universal) marca/reverte "Em verificação".
  const podeVerificar = user.perfil === 'RH' || isAdmin;
  
  // --- Estados de Layout e Dados ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  
  const [dados, setDados] = useState([]);
  const [filteredDados, setFilteredDados] = useState([]);
  const [loading, setLoading] = useState(true);
  // Recarga manual pelo botão Atualizar: busca de novo SEM desmontar a tela
  // (o `loading` cheio troca a página inteira pelo "A carregar...").
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Filtros da Tela Principal
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNome, setFilterNome] = useState('');
  // Filtro de status por CHIPS clicáveis (padrão: só Pendentes). Os chips só mostram/escondem;
  // a lista já vem com todos os elegíveis classificados pelo backend.
  const [statusFiltros, setStatusFiltros] = useState({ 'Não Preenchido': true, 'Preenchido': false, 'Em verificação': false });
  const [filterLoja, setFilterLoja] = useState('');
  // Filtro por data de elegibilidade (DATA_ELEGIVEL = admissão + 15), intervalo de/até.
  // Vazio = sem recorte naquela ponta.
  const [filtroElegivelDe, setFiltroElegivelDe] = useState('');
  const [filtroElegivelAte, setFiltroElegivelAte] = useState('');
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Ordenação por cabeçalho (null = ordem padrão: pendentes primeiro, dias desc)
  const [sortConfig, setSortConfig] = useState(null);

  // Identifica se é Gerente para ocultar o filtro de Loja
  const isGerente = user.perfil === 'Gerente';

  // --- FUNÇÕES AUXILIARES ---

  const limparFiltros = () => {
    setSearchTerm('');
    setFilterNome('');
    setFilterLoja('');
    setStatusFiltros({ 'Não Preenchido': true, 'Preenchido': false, 'Em verificação': false });
    setFiltroElegivelDe('');
    setFiltroElegivelAte('');
  };

  // Liga/desliga um chip de status.
  const toggleStatus = (chave) => setStatusFiltros(prev => ({ ...prev, [chave]: !prev[chave] }));

  // 1. Efeito de Debounce para o Nome
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilterNome(searchTerm);
    }, 300); 
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // 2. Efeito de Filtragem e Ordenação Automática
  useEffect(() => {
    let filtered = [...dados];

    if (!isGerente && filterLoja) {
      filtered = filtered.filter(d => d.LOJA === filterLoja);
    }
    if (filterNome) {
      filtered = filtered.filter(d =>
        d.NOME_FUNCIONARIO.toLowerCase().includes(filterNome.toLowerCase())
      );
    }
    // Chips de status: mostra os status ligados. Se nenhum estiver ligado, mostra todos
    // (evita lista vazia sem querer).
    const ligados = Object.keys(statusFiltros).filter(k => statusFiltros[k]);
    if (ligados.length > 0) {
      filtered = filtered.filter(d => statusFiltros[d.STATUS]);
    }
    // Recorte por data de elegibilidade. DATA_ELEGIVEL chega como string 'AAAA-MM-DD'
    // (mesmo formato do <input type="date">), então comparar string já ordena certo —
    // sem new Date(), sem risco de fuso deslocar o dia.
    if (filtroElegivelDe) {
      filtered = filtered.filter(d => d.DATA_ELEGIVEL && d.DATA_ELEGIVEL >= filtroElegivelDe);
    }
    if (filtroElegivelAte) {
      filtered = filtered.filter(d => d.DATA_ELEGIVEL && d.DATA_ELEGIVEL <= filtroElegivelAte);
    }

    // Ordenação: Pendentes primeiro, depois Dias de Empresa
    filtered.sort((a, b) => {
      if (a.STATUS !== 'Preenchido' && b.STATUS === 'Preenchido') return -1;
      if (a.STATUS === 'Preenchido' && b.STATUS !== 'Preenchido') return 1;
      return b.DIAS_EMPRESA - a.DIAS_EMPRESA;
    });

    setFilteredDados(filtered);
    setCurrentPage(1);
  }, [filterNome, statusFiltros, filterLoja, filtroElegivelDe, filtroElegivelAte, dados, isGerente]);

  // 3. Monitorar redimensionamento da tela para ajustar Sidebar
  useEffect(() => {
    const handleResize = () => { 
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setIsCollapsed(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 4. Carregar Dados
  useEffect(() => {
    fetchData();
  }, []);

  // silencioso=true (botão Atualizar): recarrega os dados mantendo a tabela na tela,
  // só desabilitando o botão enquanto busca.
  const fetchData = async ({ silencioso = false } = {}) => {
    if (silencioso) setRefreshing(true); else setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/forms-rh/primeiro15/consulta-pendentes', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Falha: ${response.status}`);
      const data = await response.json();
      // Backend devolve { dados, resumo }, mas os cards são recalculados no
      // cliente (respeitando os filtros da tela), então o resumo é ignorado.
      const lista = Array.isArray(data) ? data : (data.dados || []);
      setDados(lista);
      setFilteredDados(lista);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // --- Lógica do Histórico ---
  const handleVerHistorico = async (item) => {
    setFuncionarioSelecionado(item.NOME_FUNCIONARIO);
    setFiltroDataLog('');
    setFiltroTipoLog('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/forms-rh/primeiro15/historico-logs/${item.ID_FUNCIONARIO}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setHistorico(data);
      setHistoricoFiltrado(data);
      setShowModal(true);
    } catch (err) {
      alert("Erro ao carregar histórico: " + err.message);
    }
  };

  const aplicarFiltrosLog = () => {
    let filtrado = [...historico];
    if (filtroDataLog) {
      filtrado = filtrado.filter(log => log.DATA_ENVIO.startsWith(filtroDataLog));
    }
    if (filtroTipoLog) {
      filtrado = filtrado.filter(log => log.TIPO_ENVIO === filtroTipoLog);
    }
    setHistoricoFiltrado(filtrado);
  };

  const handleGerarLink = async (item, acao) => {
    const { ID_FUNCIONARIO, NOME_FUNCIONARIO, EMAIL, TELEFONE_CELULAR } = item; 

    if (loadingAcaoId === ID_FUNCIONARIO) return;

    setLoadingAcaoId(ID_FUNCIONARIO);

    try {
      const token = localStorage.getItem('authToken');
      
      if (acao === 'whatsapp') {
        if (!TELEFONE_CELULAR) return alert("Funcionário sem celular cadastrado.");
        const response = await fetch('/api/forms-rh/primeiro15/enviar-whatsapp-link', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: ID_FUNCIONARIO, telefone: TELEFONE_CELULAR, nome: NOME_FUNCIONARIO })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message);
        alert(data.message);

      } else if (acao === 'email') {
        if (!EMAIL) return alert("Funcionário sem e-mail cadastrado.");
        const response = await fetch('/api/forms-rh/primeiro15/enviar-email-link', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: ID_FUNCIONARIO, email: EMAIL, nome: NOME_FUNCIONARIO })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message);
        alert(data.message);
      }
    } catch (err) { 
        alert(`Erro: ${err.message}`); 
    } finally {
        setLoadingAcaoId(null);
    }
  };

  // Marca colaborador como "Em verificação" (some da cobrança por 7 dias) e recarrega.
  const handleMarcarVerificacao = async (item) => {
    if (!window.confirm(`Marcar "${item.NOME_FUNCIONARIO}" como "Em verificação"?\n\nEle sai da cobrança (lista, e-mail e WhatsApp) e volta sozinho ao radar em 7 dias.`)) return;
    setLoadingAcaoId(item.ID_FUNCIONARIO);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/forms-rh/primeiro15/marcar-verificacao', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.ID_FUNCIONARIO })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      await fetchData();
    } catch (err) {
      alert(`Erro: ${err.message}`);
    } finally {
      setLoadingAcaoId(null);
    }
  };

  // Reverte a marcação: o colaborador volta ao radar na hora.
  const handleReverter = async (item) => {
    if (!window.confirm(`Reverter "${item.NOME_FUNCIONARIO}"?\n\nEle volta para a lista de pendências imediatamente.`)) return;
    setLoadingAcaoId(item.ID_FUNCIONARIO);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/forms-rh/primeiro15/reverter-verificacao', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.ID_FUNCIONARIO })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      await fetchData();
    } catch (err) {
      alert(`Erro: ${err.message}`);
    } finally {
      setLoadingAcaoId(null);
    }
  };

  // Padronização do formato de data
  const formatarData = (dataIso) => {
    if (!dataIso) return '';
    const data = new Date(dataIso);
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  };

  // --- Ordenação por cabeçalho ---
  const handleSort = (key) => {
    setSortConfig(prev =>
      prev && prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
    setCurrentPage(1);
  };

  // Aplica a ordenação escolhida; se nenhuma, mantém a ordem padrão do filtro
  const getSorted = (arr) => {
    if (!sortConfig) return arr;
    const { key, dir } = sortConfig;
    const mult = dir === 'asc' ? 1 : -1;
    return [...arr].sort((a, b) => {
      if (key === 'DIAS_EMPRESA') return (a.DIAS_EMPRESA - b.DIAS_EMPRESA) * mult;
      const va = (a[key] ?? '').toString().toLowerCase();
      const vb = (b[key] ?? '').toString().toLowerCase();
      return va < vb ? -mult : va > vb ? mult : 0;
    });
  };

  // Ícone de ordenação no cabeçalho
  const SortIcon = ({ col }) => {
    if (!sortConfig || sortConfig.key !== col) return <ChevronsUpDown size={14} color="#cbd5e1" />;
    return sortConfig.dir === 'asc' ? <ChevronUp size={14} color="#0f172a" /> : <ChevronDown size={14} color="#0f172a" />;
  };

  // --- Cards de totais ---
  // Recalculados no cliente sobre `dados`, respeitando os MESMOS filtros da lista
  // (nome + loja + data de elegibilidade), porém IGNORANDO os chips de status.
  // Por quê ignorar os chips: chip e card são a mesma dimensão (status); se o card
  // seguisse o chip, clicar "só Pendentes" zeraria os cards de Preenchido/Em
  // verificação. Assim o card diz "nesta loja/período há N pendentes" e o chip
  // serve só pra focar a lista. (Decisão Rodrigo, 2026-06-16.)
  const dadosParaCards = dados.filter(d => {
    if (!isGerente && filterLoja && d.LOJA !== filterLoja) return false;
    if (filterNome && !d.NOME_FUNCIONARIO.toLowerCase().includes(filterNome.toLowerCase())) return false;
    if (filtroElegivelDe && !(d.DATA_ELEGIVEL && d.DATA_ELEGIVEL >= filtroElegivelDe)) return false;
    if (filtroElegivelAte && !(d.DATA_ELEGIVEL && d.DATA_ELEGIVEL <= filtroElegivelAte)) return false;
    return true;
  });
  const totalElegiveis     = dadosParaCards.length;
  const totalPreenchidos   = dadosParaCards.filter(d => d.STATUS === 'Preenchido').length;
  const totalEmVerificacao = dadosParaCards.filter(d => d.STATUS === 'Em verificação').length;
  const totalPendentes     = dadosParaCards.filter(d => d.STATUS === 'Não Preenchido').length;
  const pctPreenchido      = totalElegiveis ? Math.round((totalPreenchidos / totalElegiveis) * 100) : 0;

  // --- Paginação (sobre a lista já ordenada) ---
  const sortedDados = getSorted(filteredDados);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedDados.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedDados.length / itemsPerPage);

  const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

  // Card de total (padrão AdminUsuarios)
  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', borderLeft: `4px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <p style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', fontWeight: '800', margin: '0 0 4px 0' }}>{title}</p>
        <p style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{value}</p>
      </div>
      <div style={{ padding: '12px', borderRadius: '50%', backgroundColor: bgColor, color: color }}>
        <Icon size={20} />
      </div>
    </div>
  );

  // Chip de status clicável (liga/desliga). Ligado = preenchido na cor; desligado = contorno cinza.
  const StatusChip = ({ chave, rotulo, cor, corBg }) => {
    const ativo = statusFiltros[chave];
    return (
      <button
        onClick={() => toggleStatus(chave)}
        title={ativo ? 'Clique para ocultar' : 'Clique para mostrar'}
        style={{
          padding: '8px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
          border: `1px solid ${ativo ? cor : '#cbd5e1'}`,
          backgroundColor: ativo ? corBg : '#fff',
          color: ativo ? cor : '#94a3b8',
          display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s'
        }}
      >
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ativo ? cor : '#cbd5e1', display: 'inline-block' }} />
        {rotulo}
      </button>
    );
  };

  // Cabeçalho clicável de ordenação
  const Th = ({ col, children }) => (
    <th onClick={() => handleSort(col)} style={{ padding: '16px 24px', fontWeight: '600', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{children} <SortIcon col={col} /></span>
    </th>
  );

  // Badge do tipo de disparo (mesmas cores do histórico)
  const TipoBadge = ({ tipo }) => {
    if (!tipo) return <span style={{ color: '#cbd5e1' }}>—</span>;
    const cores = {
      WhatsApp: { bg: '#dcfce7', fg: '#166534' },
      Email:    { bg: '#e0f2fe', fg: '#0369a1' },
      Copia:    { bg: '#f1f5f9', fg: '#475569' },
    };
    const c = cores[tipo] || cores.Copia;
    return <span style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', backgroundColor: c.bg, color: c.fg }}>{tipo}</span>;
  };

  // Botão de paginação (padrão do módulo Auditoria)
  const btnPag = (desabilitado) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '8px 12px',
    background: desabilitado ? '#f1f5f9' : '#fff',
    color: desabilitado ? '#94a3b8' : '#475569',
    border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '700', fontSize: '13px',
    cursor: desabilitado ? 'not-allowed' : 'pointer',
  });

  if (loading) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}><h2>A carregar...</h2></div>;

  return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

      <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

        {!embedded && (
          /* Navbar Simples Embutida */
          <div style={{
            height: '80px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            padding: isMobile ? '0 20px 0 60px' : '0 40px',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Consulta de Pendências</h2>
          </div>
        )}

        {/* LARGURA MÁXIMA AUMENTADA PARA 100% */}
        <div style={{ padding: isMobile ? '20px' : '40px', maxWidth: '100%', margin: '0 auto' }}>
            
          <div style={{ marginBottom: '30px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>Meus Primeiros 15 Dias</h1>
            <p style={{ color: '#64748b', fontSize: '16px' }}>Gestão de preenchimento e disparos de links para funcionários (Crew, Estoquista, Promotor de Vendas e ASG) com 15+ dias.</p>
          </div>

          {/* --- Cards de totais (refletem os filtros ativos) --- */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <StatCard title="Elegíveis"       value={totalElegiveis}     icon={Users}        color="#3b82f6" bgColor="#eff6ff" />
            <StatCard title="Pendentes"       value={totalPendentes}     icon={Clock}        color="#f43f5e" bgColor="#fff1f2" />
            <StatCard title="Em verificação"  value={totalEmVerificacao} icon={PauseCircle}  color="#f59e0b" bgColor="#fffbeb" />
            <StatCard title="Preenchidos"     value={totalPreenchidos}   icon={CheckCircle2} color="#10b981" bgColor="#ecfdf5" />
            <StatCard title="% Preenchido"    value={`${pctPreenchido}%`} icon={Percent}     color="#8b5cf6" bgColor="#f5f3ff" />
          </div>

          {/* --- Filtros --- */}
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '30px', alignItems: 'flex-end' }}>
            {/* Coluna 1: Loja em cima e Funcionário embaixo (empilhados). Os dropdowns são
                nativos do navegador — abrem por cima do que estiver abaixo, sem cortar. */}
            <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {!isGerente && (
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Loja</label>
                  <select value={filterLoja} onChange={(e) => setFilterLoja(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}>
                    <option value="">Todas</option>
                    <option value="ANV">ANV</option>
                    <option value="BVV">BVV</option>
                    <option value="FER">FER</option>
                    <option value="GTV">GTV</option>
                    <option value="LIN">LIN</option>
                    <option value="MAL">MAL</option>
                    <option value="MOX">MOX</option>
                    <option value="PCI">PCI</option>
                    <option value="PCS">PCS</option>
                    <option value="RDP">RDP</option>
                    <option value="SED">SED</option>
                    <option value="SVV">SVV</option>
                    <option value="VEL">VEL</option>
                    <option value="VSH">VSH</option>
                  </select>
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Funcionário</label>
                <div style={{ position: 'relative' }}>
                  <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '11px' }} />
                  <input type="text" list="lista-funcionarios" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Pesquisar por nome..." style={{ width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }} />
                  <datalist id="lista-funcionarios">{dados.map((d, idx) => <option key={idx} value={d.NOME_FUNCIONARIO} />)}</datalist>
                </div>
              </div>
            </div>

            <div style={{ flex: '2 1 320px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Status (clique para mostrar/ocultar)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <StatusChip chave="Não Preenchido" rotulo="Pendentes"      cor="#b91c1c" corBg="#fee2e2" />
                <StatusChip chave="Em verificação" rotulo="Em verificação" cor="#b45309" corBg="#fef3c7" />
                <StatusChip chave="Preenchido"     rotulo="Preenchidos"    cor="#166534" corBg="#dcfce7" />
              </div>
            </div>

            {/* Recorte por Data Pesquisa = quando ficou elegível (admissão + 15). Vazio = sem recorte. */}
            <div style={{ flex: '1 1 300px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Data Pesquisa entre</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="date" value={filtroElegivelDe} onChange={(e) => setFiltroElegivelDe(e.target.value)} style={{ flex: 1, minWidth: '120px', padding: '9px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569' }} />
                <span style={{ color: '#94a3b8', fontSize: '13px' }}>até</span>
                <input type="date" value={filtroElegivelAte} onChange={(e) => setFiltroElegivelAte(e.target.value)} style={{ flex: 1, minWidth: '120px', padding: '9px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569' }} />
              </div>
            </div>

            {/* Atualizar em cima e Limpar embaixo (empilhados, como Loja/Funcionário) */}
            <div style={{ flex: '0 1 auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Recarrega a tabela sem dar F5 (busca silenciosa: a tela não pisca) */}
              <button onClick={() => fetchData({ silencioso: true })} disabled={refreshing} title="Recarregar os dados da tabela" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '600', cursor: refreshing ? 'wait' : 'pointer', opacity: refreshing ? 0.6 : 1 }}>
                <RefreshCw size={16} /> {refreshing ? 'Atualizando…' : 'Atualizar'}
              </button>
              <button onClick={limparFiltros} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                <Filter size={16} /> Limpar
              </button>
            </div>
          </div>

          {/* TABELA DE DADOS MODERNIZADA */}
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '1000px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', color: '#64748b', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <Th col="LOJA">Loja</Th>
                    <Th col="NOME_FUNCIONARIO">Nome</Th>
                    <Th col="DIAS_EMPRESA">Dias</Th>
                    <Th col="DATA_ELEGIVEL">Data Pesquisa</Th>
                    <Th col="STATUS">Status</Th>
                    <Th col="ULTIMO_DISPARO">Último Envio</Th>
                    <th style={{ padding: '16px 24px', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Tipo</th>
                    <th style={{ padding: '16px 24px', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Situação</th>
                    <th style={{ padding: '16px 24px', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={{ padding: '16px 24px', fontWeight: '600', fontSize: '14px', color: '#0f172a' }}>{item.LOJA}</td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#334155' }}>{item.NOME_FUNCIONARIO}</td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#64748b' }}>{item.DIAS_EMPRESA}</td>
                      {/* DATA_ELEGIVEL é string 'AAAA-MM-DD' — montar dd/mm/aaaa direto da string
                          (formatarData usaria new Date() e o fuso poderia voltar um dia). */}
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {item.DATA_ELEGIVEL ? item.DATA_ELEGIVEL.split('-').reverse().join('/') : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        {(() => {
                          const cores = {
                            'Preenchido':     { fg: '#166534', bg: '#dcfce7' },
                            'Em verificação': { fg: '#b45309', bg: '#fef3c7' },
                            'Não Preenchido': { fg: '#b91c1c', bg: '#fee2e2' },
                          };
                          const c = cores[item.STATUS] || cores['Não Preenchido'];
                          // Tooltip com quem marcou / quando, só para "Em verificação"
                          const tip = item.STATUS === 'Em verificação' && item.MARCADO_POR_NOME
                            ? `Marcado por ${item.MARCADO_POR_NOME} em ${formatarData(item.DATA_MARCACAO)}`
                            : undefined;
                          return (
                            <div>
                              <span title={tip} style={{
                                color: c.fg, fontWeight: '600', padding: '6px 14px', fontSize: '13px',
                                backgroundColor: c.bg, borderRadius: '999px', whiteSpace: 'nowrap', display: 'inline-block'
                              }}>
                                {item.STATUS}
                              </span>
                              {item.STATUS === 'Em verificação' && item.DIAS_PARA_VOLTAR != null && (
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                  volta em {Math.max(0, item.DIAS_PARA_VOLTAR)} dia(s)
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {item.ULTIMO_DISPARO ? formatarData(item.ULTIMO_DISPARO) : <span style={{ color: '#cbd5e1' }}>Nunca</span>}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <TipoBadge tipo={item.ULTIMO_TIPO} />
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        {item.ULTIMO_STATUS ? (() => {
                          // Situação do último disparo: verde=Sucesso, vermelho=Erro, âmbar=Não enviado.
                          const cores = {
                            'Sucesso':     { bg: '#dcfce7', fg: '#166534' },
                            'Erro':        { bg: '#fee2e2', fg: '#b91c1c' },
                            'Não enviado': { bg: '#fef3c7', fg: '#b45309' },
                          };
                          const c = cores[item.ULTIMO_STATUS] || { bg: '#f1f5f9', fg: '#475569' };
                          return <span style={{ padding: '4px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', backgroundColor: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{item.ULTIMO_STATUS}</span>;
                        })() : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          {item.STATUS === 'Não Preenchido' && (
                            <>
                              {isAdminOrRH && (
                                <button
                                  onClick={() => handleGerarLink(item, 'whatsapp')}
                                  disabled={loadingAcaoId === item.ID_FUNCIONARIO}
                                  title="Enviar link por WhatsApp"
                                  style={{ background: 'none', border: 'none', padding: '2px', display: 'flex', cursor: loadingAcaoId === item.ID_FUNCIONARIO ? 'not-allowed' : 'pointer', opacity: loadingAcaoId === item.ID_FUNCIONARIO ? 0.5 : 1 }}
                                >
                                  <MessageCircle size={17} color="#25D366" />
                                </button>
                              )}
                              <button onClick={() => handleGerarLink(item, 'email')} title="Enviar link por e-mail" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex' }}>
                                <Mail size={17} color="#64748b" />
                              </button>
                              {/* Marcar como "Em verificação" (some da cobrança por 7 dias) — só RH/Admin */}
                              {podeVerificar && (
                                <button
                                  onClick={() => handleMarcarVerificacao(item)}
                                  disabled={loadingAcaoId === item.ID_FUNCIONARIO}
                                  title='Marcar como "Em verificação" (some da cobrança por 7 dias)'
                                  style={{ background: 'none', border: 'none', padding: '2px', display: 'flex', cursor: loadingAcaoId === item.ID_FUNCIONARIO ? 'not-allowed' : 'pointer', opacity: loadingAcaoId === item.ID_FUNCIONARIO ? 0.5 : 1 }}
                                >
                                  <EyeOff size={17} color="#f59e0b" />
                                </button>
                              )}
                            </>
                          )}
                          {item.STATUS === 'Em verificação' && podeVerificar && (
                            <button
                              onClick={() => handleReverter(item)}
                              disabled={loadingAcaoId === item.ID_FUNCIONARIO}
                              title="Reverter (voltar para pendências agora)"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '6px 12px', color: '#b45309', fontWeight: '700', fontSize: '12px', cursor: loadingAcaoId === item.ID_FUNCIONARIO ? 'not-allowed' : 'pointer', opacity: loadingAcaoId === item.ID_FUNCIONARIO ? 0.5 : 1 }}
                            >
                              <RotateCcw size={14} /> Reverter
                            </button>
                          )}
                          {/* Histórico visível para todos que acessam a tela (inclui Gerente) — 2026-06-18 */}
                          {(isAdminOrRH || isGerente) && (
                            <button onClick={() => handleVerHistorico(item)} title="Ver histórico de disparos" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex' }}>
                              <History size={16} color="#475569" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* PAGINAÇÃO (padrão Auditoria) */}
            <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: '#475569' }}>
                {sortedDados.length} registro(s) · 15 por página
                {sortedDados.length > 0 && ` · mostrando ${indexOfFirstItem + 1}–${Math.min(indexOfLastItem, sortedDados.length)}`}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={btnPag(currentPage === 1)}>« Primeira</button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={btnPag(currentPage === 1)}><ChevronLeft size={14} /> Anterior</button>
                <span style={{ padding: '8px 14px', background: '#e11d48', color: '#fff', borderRadius: '6px', fontWeight: '700', fontSize: '13px' }}>{currentPage} / {totalPages || 1}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} style={btnPag(currentPage === totalPages || totalPages === 0)}>Próxima <ChevronRight size={14} /></button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} style={btnPag(currentPage === totalPages || totalPages === 0)}>Última »</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- MODAL DE HISTÓRICO --- */}
      {showModal && (
          // Clicar no fundo (overlay) fecha; o stopPropagation no conteúdo evita fechar ao clicar dentro.
          <div onClick={() => setShowModal(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(15,23,42,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '20px' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', padding: '30px', borderRadius: '16px', width: '95%', maxWidth: '1100px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Histórico de Disparos</h3>
                <button onClick={() => setShowModal(false)} style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
              </div>
              <p style={{ color: '#475569', marginBottom: '20px' }}><strong>Funcionário:</strong> {funcionarioSelecionado}</p>

              <div style={{ display: 'flex', gap: '10px', margin: '15px 0', backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '5px', flexWrap: 'wrap' }}>
                <input type="date" value={filtroDataLog} onChange={(e) => setFiltroDataLog(e.target.value)} style={{ padding: '5px' }} />
                <select value={filtroTipoLog} onChange={(e) => setFiltroTipoLog(e.target.value)} style={{ padding: '5px' }}>
                  <option value="">Todos Tipos</option>
                  <option value="Email">Email</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Copia">Copia</option>
                </select>
                <button onClick={aplicarFiltrosLog} style={{ padding: '5px 10px', cursor: 'pointer', backgroundColor: '#333', color: 'white', border: 'none', borderRadius:'4px' }}>Filtrar</button>
              </div>

              <div style={{overflowX: 'auto'}}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#eee', textAlign: 'left' }}>
                      <th style={{ padding: '8px' }}>Data</th>
                      <th>Por</th>
                      <th>Tipo</th>
                      <th>Situação</th>
                      <th>Observação</th>
                      <th>Destino</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicoFiltrado.map((h, i) => {
                      // Cores do badge de situação: Sucesso=verde, Erro=vermelho, Não enviado=âmbar/cinza
                      const coresStatus = {
                        'Sucesso':     { bg: '#dcfce7', fg: '#166534' },
                        'Erro':        { bg: '#fee2e2', fg: '#b91c1c' },
                        'Não enviado': { bg: '#fef3c7', fg: '#b45309' },
                      };
                      const cs = coresStatus[h.STATUS_ENVIO] || { bg: '#f1f5f9', fg: '#475569' };
                      return (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{formatarData(h.DATA_ENVIO)} <br/><small>{new Date(h.DATA_ENVIO).toLocaleTimeString()}</small></td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#475569' }}>{h.GERENTE_NOME}</td>
                        <td>
                          <span style={{
                            padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold',
                            backgroundColor: h.TIPO_ENVIO === 'WhatsApp' ? '#dcfce7' : h.TIPO_ENVIO === 'Email' ? '#e0f2fe' : '#f1f5f9',
                            color: h.TIPO_ENVIO === 'WhatsApp' ? '#166534' : h.TIPO_ENVIO === 'Email' ? '#0369a1' : '#475569'
                          }}>
                            {h.TIPO_ENVIO}
                          </span>
                        </td>
                        <td>
                          <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', backgroundColor: cs.bg, color: cs.fg, whiteSpace: 'nowrap' }}>
                            {h.STATUS_ENVIO || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#475569' }}>{h.OBSERVACAO || '—'}</td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#475569' }}>{h.EMAIL_DESTINO || '—'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
             <button onClick={() => setShowModal(false)} style={{ marginTop: '20px', width: '100%', padding: '12px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>FECHAR</button>
            </div>
          </div>
      )}
    </div>
  );
}

export default Primeiro15Consulta;